import type { ChatRequest, ImageRef, SSEPayload } from '../types/api';
import { fetchWithTimeout, STREAM_CONNECT_TIMEOUT_MS, UPLOAD_TIMEOUT_MS } from './http';

const BASE = import.meta.env.VITE_API_BASE_URL ?? '';

/**
 * A non-ok API response, carrying the backend's `detail.code` when it sent one.
 *
 * The code is what lets a caller tell a rejection the user can act on (over the
 * request ceiling, this conversation is still answering) from a failure they cannot,
 * without the caller matching on status numbers or on message text.
 */
export class ApiError extends Error {
  code?: string;

  constructor(code: string | undefined, message: string) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
  }
}

async function apiError(res: Response): Promise<ApiError> {
  const body = await res.json().catch(() => null);
  return new ApiError(body?.detail?.code, body?.detail?.message ?? `HTTP ${res.status}`);
}

// Exported for its unit tests. Nothing outside this module calls it directly: the
// frame-boundary and malformed-payload handling below is the reason it is worth
// testing at all, and neither is reachable through startChat without a live stream.
export async function* readSSEBody(body: ReadableStream<Uint8Array>): AsyncGenerator<SSEPayload> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let currentEvent = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      if (line.startsWith('event: ')) {
        currentEvent = line.slice(7).trim();
      } else if (line.startsWith('data: ') && currentEvent) {
        try {
          const data = JSON.parse(line.slice(6));
          yield { event: currentEvent, data } as SSEPayload;
        } catch {
          // malformed JSON line, skip
        }
        currentEvent = '';
      }
    }
  }
}

async function* streamSSE(
  url: string,
  body: ChatRequest,
  signal: AbortSignal,
): AsyncGenerator<SSEPayload> {
  const res = await fetchWithTimeout(
    `${BASE}${url}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    },
    STREAM_CONNECT_TIMEOUT_MS,
  );

  if (!res.ok) throw await apiError(res);
  if (!res.body) throw new Error('No response body');
  yield* readSSEBody(res.body);
}

export function startChat(body: ChatRequest, signal: AbortSignal): AsyncGenerator<SSEPayload> {
  return streamSSE('/api/v1/chat/start', body, signal);
}

export function continueChat(body: ChatRequest, signal: AbortSignal): AsyncGenerator<SSEPayload> {
  return streamSSE('/api/v1/chat/continue', body, signal);
}

export async function stopChat(sessionId: string): Promise<void> {
  await fetchWithTimeout(`${BASE}/api/v1/chat/${sessionId}/stop`, { method: 'POST' });
}

export async function uploadChatImage(
  sessionId: string,
  filename: string,
  base64Data: string,
): Promise<{ image_ref: ImageRef }> {
  const res = await fetchWithTimeout(
    `${BASE}/api/v1/chat/${encodeURIComponent(sessionId)}/images`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename, data: base64Data }),
    },
    UPLOAD_TIMEOUT_MS,
  );
  if (!res.ok) throw await apiError(res);
  return res.json();
}

export async function* reconnectChatStream(
  sessionId: string,
  signal: AbortSignal,
): AsyncGenerator<SSEPayload> {
  const res = await fetchWithTimeout(
    `${BASE}/api/v1/chat/${encodeURIComponent(sessionId)}/stream`,
    { signal },
    STREAM_CONNECT_TIMEOUT_MS,
  );
  if (!res.ok) throw new Error(`Request failed: HTTP ${res.status}`);
  if (!res.body) throw new Error('No response body');
  yield* readSSEBody(res.body);
}
